<template>
  <div class="order-management">
    <div class="page-header">
      <h2>订单管理</h2>
      <div class="header-actions">
        <el-button 
          type="warning" 
          icon="el-icon-truck" 
          @click="handleBatchShip"
          :disabled="selectedOrders.length === 0"
        >
          批量发货 ({{ selectedOrders.length }})
        </el-button>
        <el-button type="primary" icon="el-icon-download" @click="handleExport" :loading="exporting">导出订单</el-button>
        <el-button type="success" icon="el-icon-s-data" @click="handleStatistics">订单统计</el-button>
      </div>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="订单号">
        <el-input v-model="searchForm.order_no" placeholder="请输入订单号" clearable style="width: 180px" />
      </el-form-item>
      <el-form-item label="店铺ID">
        <el-input v-model="searchForm.shop_id" placeholder="请输入店铺ID" clearable style="width: 120px" />
      </el-form-item>
      <el-form-item label="用户手机号">
        <el-input v-model="searchForm.user_mobile" placeholder="请输入用户手机号" clearable style="width: 150px" />
      </el-form-item>
      <el-form-item label="订单状态">
        <el-select v-model="searchForm.order_status" placeholder="请选择状态" clearable style="width: 120px">
          <el-option label="待付款" :value="0" />
          <el-option label="待发货" :value="1" />
          <el-option label="待收货" :value="2" />
          <el-option label="已完成" :value="3" />
          <el-option label="已取消" :value="4" />
          <el-option label="售后中" :value="5" />
        </el-select>
      </el-form-item>
      <el-form-item label="支付状态">
        <el-select v-model="searchForm.pay_status" placeholder="请选择支付状态" clearable style="width: 120px">
          <el-option label="未支付" :value="0" />
          <el-option label="已支付" :value="1" />
          <el-option label="已退款" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="发货状态">
        <el-select v-model="searchForm.shipping_status" placeholder="请选择发货状态" clearable style="width: 120px">
          <el-option label="未发货" :value="0" />
          <el-option label="已发货" :value="1" />
          <el-option label="已收货" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="支付方式">
        <el-select v-model="searchForm.payment_method" placeholder="请选择支付方式" clearable style="width: 120px">
          <el-option label="微信支付" value="wechat" />
          <el-option label="支付宝" value="alipay" />
          <el-option label="余额支付" value="balance" />
          <el-option label="线下核销" value="verify" />
        </el-select>
      </el-form-item>
      <el-form-item label="配送方式">
        <el-select v-model="searchForm.delivery_type" placeholder="请选择配送方式" clearable style="width: 120px">
          <el-option label="快递配送" value="express" />
          <el-option label="到店自提" value="pickup" />
        </el-select>
      </el-form-item>
      <el-form-item label="创建时间">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width: 240px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table 
      v-loading="loading" 
      :data="orderList" 
      border 
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column 
        type="selection" 
        width="55"
        :selectable="checkSelectable"
      />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="order_no" label="订单号" width="180" />
      <el-table-column prop="user_name" label="用户名" width="120" />
      <el-table-column prop="user_mobile" label="用户手机号" width="120" />
      <el-table-column prop="order_amount" label="订单金额" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.order_amount }}
        </template>
      </el-table-column>
      <el-table-column prop="paid_amount" label="实付金额" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.paid_amount }}
        </template>
      </el-table-column>
      <el-table-column prop="order_status" label="订单状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.order_status)">
            {{ getStatusText(scope.row.order_status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="pay_status" label="支付状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getPayStatusType(scope.row.pay_status)">
            {{ getPayStatusText(scope.row.pay_status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="delivery_type" label="配送方式" width="110">
        <template slot-scope="scope">
          <el-tag :type="scope.row.delivery_type === 'pickup' ? 'success' : 'primary'" size="small">
            <i :class="scope.row.delivery_type === 'pickup' ? 'el-icon-s-shop' : 'el-icon-truck'"></i>
            {{ scope.row.delivery_type === 'pickup' ? '到店自提' : '快递配送' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button 
              v-if="scope.row.order_status == 1"
              size="mini" 
              type="text" 
              class="ship-btn"
              @click="handleShip(scope.row)"
            >
              发货
            </el-button>
            <el-button 
              v-if="scope.row.order_status == 1" 
              size="mini" 
              type="text" 
              class="cancel-btn"
              @click="handleCancel(scope.row)"
            >
              取消
            </el-button>
            <el-button
              v-if="scope.row.order_status == 5"
              size="mini"
              type="text"
              class="approve-btn"
              @click="handleApproveRefund(scope.row)"
            >
              同意退款
            </el-button>
            <el-button
              v-if="scope.row.order_status == 5"
              size="mini"
              type="text"
              class="reject-btn"
              @click="handleRejectRefund(scope.row)"
            >
              拒绝退款
            </el-button>
            <el-button
              v-if="scope.row.order_status == 5"
              size="mini"
              type="text"
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 查看详情对话框 -->
    <el-dialog title="订单详情" :visible.sync="viewDialogVisible" width="900px">
      <div v-if="currentOrder" class="order-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">订单ID</div>
              <div class="detail-value">{{ currentOrder.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">订单号</div>
              <div class="detail-value">{{ currentOrder.order_no }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">订单状态</div>
              <div class="detail-value">
                <el-tag :type="getStatusType(currentOrder.order_status)">
                  {{ getStatusText(currentOrder.order_status) }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">支付状态</div>
              <div class="detail-value">
                <el-tag :type="getPayStatusType(currentOrder.pay_status)">
                  {{ getPayStatusText(currentOrder.pay_status) }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">发货状态</div>
              <div class="detail-value">
                <el-tag :type="getShippingStatusType(currentOrder.shipping_status)">
                  {{ getShippingStatusText(currentOrder.shipping_status) }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">支付方式</div>
              <div class="detail-value">{{ getPaymentMethodText(currentOrder.payment_method) }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">配送方式</div>
              <div class="detail-value">
                <el-tag :type="currentOrder.delivery_type === 'pickup' ? 'success' : 'primary'" size="small">
                  <i :class="currentOrder.delivery_type === 'pickup' ? 'el-icon-s-shop' : 'el-icon-truck'"></i>
                  {{ currentOrder.delivery_type === 'pickup' ? '到店自提' : '快递配送' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 用户信息 -->
        <div class="detail-section">
          <div class="section-title">{{ currentOrder.delivery_type === 'pickup' ? '自提信息' : '收货信息' }}</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">用户ID</div>
              <div class="detail-value">{{ currentOrder.user_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户名</div>
              <div class="detail-value">{{ currentOrder.user_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户手机号</div>
              <div class="detail-value">{{ currentOrder.user_mobile || '-' }}</div>
            </div>
            <div v-if="currentOrder.delivery_type === 'express'" class="detail-row">
              <div class="detail-label">收货地址</div>
              <div class="detail-value">
                {{ (currentOrder.receiver_province || '') + (currentOrder.receiver_city || '') + (currentOrder.receiver_district || '') + (currentOrder.receiver_address || '') || '-' }}
              </div>
            </div>
            <div v-else class="detail-row">
              <div class="detail-label">自提地址</div>
              <div class="detail-value">{{ currentOrder.receiver_address || currentOrder.shop_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">{{ currentOrder.delivery_type === 'pickup' ? '联系人' : '收货人' }}</div>
              <div class="detail-value">{{ currentOrder.receiver_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">{{ currentOrder.delivery_type === 'pickup' ? '联系电话' : '收货电话' }}</div>
              <div class="detail-value">{{ currentOrder.receiver_phone || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 金额信息 -->
        <div class="detail-section">
          <div class="section-title">金额信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">商品总额</div>
              <div class="detail-value amount-text">¥{{ currentOrder.goods_amount || '0.00' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">优惠券金额</div>
              <div class="detail-value">-¥{{ currentOrder.coupon_amount || '0.00' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">运费</div>
              <div class="detail-value" :class="{ 'free-shipping': currentOrder.delivery_type === 'pickup' }">
                {{ currentOrder.delivery_type === 'pickup' ? '¥0.00（自提免运费）' : '¥' + (currentOrder.shipping_fee || '0.00') }}
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">优惠金额</div>
              <div class="detail-value">-¥{{ currentOrder.discount_amount || '0.00' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">订单总额</div>
              <div class="detail-value amount-text">¥{{ currentOrder.order_amount }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">实付金额</div>
              <div class="detail-value amount-highlight">¥{{ currentOrder.paid_amount }}</div>
            </div>
          </div>
        </div>

        <!-- 商品信息 -->
        <div class="detail-section">
          <div class="section-title">商品信息</div>
          <div class="goods-list">
            <el-table :data="currentOrder.goods_list || []" border>
              <el-table-column prop="goods_image" label="商品图片" width="100">
                <template slot-scope="scope">
                  <el-image 
                    v-if="scope.row.goods_image" 
                    :src="scope.row.goods_image" 
                    style="width: 60px; height: 60px; border-radius: 4px; cursor: pointer;"
                    fit="cover"
                    :preview-src-list="[scope.row.goods_image]"
                  />
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="goods_name" label="商品名称" />
              <el-table-column prop="goods_spec" label="规格" width="150">
                <template slot-scope="scope">
                  {{ scope.row.goods_spec || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="goods_price" label="单价" width="100">
                <template slot-scope="scope">
                  ¥{{ scope.row.goods_price }}
                </template>
              </el-table-column>
              <el-table-column prop="goods_number" label="数量" width="80" align="center" />
              <el-table-column prop="goods_amount" label="小计" width="120">
                <template slot-scope="scope">
                  <span class="amount-text">¥{{ scope.row.goods_amount }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- 物流信息 -->
        <div v-if="currentOrder.shipping_no && currentOrder.delivery_type === 'express'" class="detail-section">
          <div class="section-title">物流信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">快递公司</div>
              <div class="detail-value">{{ currentOrder.shipping_company || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">快递单号</div>
              <div class="detail-value">{{ currentOrder.shipping_no || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">发货时间</div>
              <div class="detail-value">{{ currentOrder.shipping_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">发货备注</div>
              <div class="detail-value">{{ currentOrder.admin_note || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 用户备注 -->
        <div class="detail-section" v-if="currentOrder.order_note">
          <div class="section-title">用户备注</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">订单备注</div>
              <div class="detail-value remark-content">{{ currentOrder.order_note }}</div>
            </div>
          </div>
        </div>

        <!-- 管理员备注 -->
        <div class="detail-section" v-if="currentOrder.admin_note">
          <div class="section-title">管理员备注</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">备注内容</div>
              <div class="detail-value remark-content admin-remark">{{ currentOrder.admin_note }}</div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店铺ID</div>
              <div class="detail-value">{{ currentOrder.shop_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺名称</div>
              <div class="detail-value">{{ currentOrder.shop_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">取消时间</div>
              <div class="detail-value">{{ currentOrder.cancel_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">取消原因</div>
              <div class="detail-value">{{ currentOrder.cancel_reason || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">收货时间</div>
              <div class="detail-value">{{ currentOrder.receive_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">完成时间</div>
              <div class="detail-value">{{ currentOrder.finish_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">支付时间</div>
              <div class="detail-value">{{ currentOrder.payment_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentOrder.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentOrder.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button 
          v-if="currentOrder && currentOrder.order_status == 1"
          type="success" 
          @click="handleShipFromDetail"
        >
          发货
        </el-button>
      </div>
    </el-dialog>

    <!-- 发货对话框 -->
    <el-dialog title="订单发货" :visible.sync="shipDialogVisible" width="500px">
      <el-form ref="shipForm" :model="shipForm" :rules="shipRules" label-width="100px">
        <el-form-item label="快递公司" prop="shipping_company">
          <el-select v-model="shipForm.shipping_company" placeholder="请选择快递公司" style="width: 100%">
            <el-option label="顺丰速运" value="顺丰速运" />
            <el-option label="圆通速递" value="圆通速递" />
            <el-option label="中通快递" value="中通快递" />
            <el-option label="申通快递" value="申通快递" />
            <el-option label="韵达快递" value="韵达快递" />
            <el-option label="邮政EMS" value="邮政EMS" />
            <el-option label="德邦快递" value="德邦快递" />
          </el-select>
        </el-form-item>
        <el-form-item label="快递单号" prop="shipping_no">
          <el-input v-model="shipForm.shipping_no" placeholder="请输入快递单号" />
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input v-model="shipForm.admin_note" type="textarea" :rows="3" placeholder="请输入发货备注" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="shipDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleShipSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 批量发货对话框 -->
    <el-dialog title="批量发货" :visible.sync="batchShipDialogVisible" width="600px">
      <el-alert
        title="提示"
        type="info"
        :closable="false"
        style="margin-bottom: 20px"
      >
        已选择 <strong>{{ selectedOrders.length }}</strong> 个订单，请填写物流信息进行批量发货
      </el-alert>
      
      <el-form ref="batchShipForm" :model="batchShipForm" :rules="batchShipRules" label-width="100px">
        <el-form-item label="快递公司" prop="shipping_company">
          <el-select v-model="batchShipForm.shipping_company" placeholder="请选择快递公司" style="width: 100%">
            <el-option label="顺丰速运" value="顺丰速运" />
            <el-option label="圆通速递" value="圆通速递" />
            <el-option label="中通快递" value="中通快递" />
            <el-option label="申通快递" value="申通快递" />
            <el-option label="韵达快递" value="韵达快递" />
            <el-option label="邮政EMS" value="邮政EMS" />
            <el-option label="德邦快递" value="德邦快递" />
          </el-select>
        </el-form-item>
        <el-form-item label="快递单号" prop="shipping_no">
          <el-input 
            v-model="batchShipForm.shipping_no" 
            placeholder="请输入快递单号（批量发货使用同一单号）" 
          />
          <div class="form-tip">
            <i class="el-icon-info"></i>
            批量发货将为所有选中订单使用相同的物流信息
          </div>
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input 
            v-model="batchShipForm.admin_note" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入发货备注（选填）" 
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchShipDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleBatchShipSubmit" :loading="batchShipping">
          确认发货
        </el-button>
      </div>
    </el-dialog>

    <!-- 订单统计对话框 -->
    <el-dialog title="订单统计" :visible.sync="statisticsDialogVisible" width="800px">
      <div v-if="statisticsData" class="statistics-content">
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="statistics-card">
              <div class="statistics-label">订单总数</div>
              <div class="statistics-value primary">{{ statisticsData.total || 0 }}</div>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="statistics-card">
              <div class="statistics-label">订单总金额</div>
              <div class="statistics-value success">¥{{ (statisticsData.total_amount || 0).toFixed(2) }}</div>
            </div>
          </el-col>
        </el-row>
        
        <el-row :gutter="20" style="margin-top: 20px;">
          <el-col :span="12">
            <div class="statistics-card">
              <div class="statistics-label">已支付订单数</div>
              <div class="statistics-value info">{{ statisticsData.paid_count || 0 }}</div>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="statistics-card">
              <div class="statistics-label">已支付金额</div>
              <div class="statistics-value success">¥{{ (statisticsData.paid_amount || 0).toFixed(2) }}</div>
            </div>
          </el-col>
        </el-row>

        <div class="statistics-section">
          <div class="section-title">订单状态统计</div>
          <el-table :data="statusStatistics" border style="width: 100%">
            <el-table-column prop="label" label="订单状态" width="150" />
            <el-table-column prop="count" label="订单数量" width="120" align="right">
              <template slot-scope="scope">
                <span class="count-text">{{ scope.row.count }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="订单金额" align="right">
              <template slot-scope="scope">
                <span class="amount-text">¥{{ scope.row.amount.toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="percentage" label="占比" width="120" align="right">
              <template slot-scope="scope">
                <el-progress :percentage="scope.row.percentage" :color="scope.row.color" />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="statistics-section">
          <div class="section-title">支付方式统计</div>
          <el-table :data="paymentStatistics" border style="width: 100%">
            <el-table-column prop="label" label="支付方式" width="150" />
            <el-table-column prop="count" label="订单数量" width="120" align="right">
              <template slot-scope="scope">
                <span class="count-text">{{ scope.row.count }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="支付金额" align="right">
              <template slot-scope="scope">
                <span class="amount-text">¥{{ scope.row.amount.toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="percentage" label="占比" width="120" align="right">
              <template slot-scope="scope">
                <el-progress :percentage="scope.row.percentage" :color="scope.row.color" />
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="statisticsDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 同意退款对话框 -->
    <el-dialog title="同意退款" :visible.sync="approveRefundDialogVisible" width="500px">
      <el-form ref="approveRefundForm" :model="approveRefundForm" :rules="approveRefundRules" label-width="100px">
        <el-form-item label="退款金额" prop="refund_amount">
          <el-input v-model="approveRefundForm.refund_amount" type="number" placeholder="请输入退款金额">
            <template slot="prepend">¥</template>
          </el-input>
          <div class="form-tip">最高可退 ¥{{ currentOrder && currentOrder.paid_amount }}</div>
        </el-form-item>
        <el-form-item label="管理员备注">
          <el-input v-model="approveRefundForm.admin_note" type="textarea" :rows="3" placeholder="请输入备注（选填）" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="approveRefundDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleApproveRefundSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderList, getOrderDetail, shipOrder, batchShipOrder, cancelOrder, deleteOrder, getOrderStatistics, approveRefund, rejectRefund } from '@/api/store'
import { downloadFile } from '@/utils/request'

export default {
  name: 'OrderManagement',
  data() {
    return {
      loading: false,
      orderList: [],
      selectedOrders: [],
      searchForm: {
        order_no: '',
        shop_id: '',
        user_mobile: '',
        order_status: '',
        pay_status: '',
        shipping_status: '',
        payment_method: '',
        delivery_type: '' // 配送方式
      },
      dateRange: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      viewDialogVisible: false,
      shipDialogVisible: false,
      batchShipDialogVisible: false,
      statisticsDialogVisible: false,
      currentOrder: null,
      statisticsData: null,
      exporting: false,
      batchShipping: false,
      shipForm: {
        id: null,
        shipping_company: '',
        shipping_no: '',
        admin_note: ''
      },
      shipRules: {
        shipping_company: [
          { required: true, message: '请选择快递公司', trigger: 'change' }
        ],
        shipping_no: [
          { required: true, message: '请输入快递单号', trigger: 'blur' }
        ]
      },
      batchShipForm: {
        ids: [],
        shipping_company: '',
        shipping_no: '',
        admin_note: ''
      },
      batchShipRules: {
        shipping_company: [
          { required: true, message: '请选择快递公司', trigger: 'change' }
        ],
        shipping_no: [
          { required: true, message: '请输入快递单号', trigger: 'blur' }
        ]
      },
      approveRefundDialogVisible: false,
      approveRefundForm: {
        id: null,
        refund_amount: '',
        admin_note: ''
      },
      approveRefundRules: {
        refund_amount: [
          { required: true, message: '请输入退款金额', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const num = parseFloat(value)
              if (isNaN(num)) {
                callback(new Error('退款金额必须为数字'))
              } else if (num <= 0) {
                callback(new Error('退款金额必须大于0'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchOrderList()
  },
  methods: {
    async fetchOrderList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        // 处理时间范围
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }
        const response = await getOrderList(params)
        this.orderList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取订单列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchOrderList()
    },
    handleReset() {
      this.searchForm = {
        order_no: '',
        shop_id: '',
        user_mobile: '',
        order_status: '',
        pay_status: '',
        shipping_status: '',
        payment_method: '',
        delivery_type: ''
      }
      this.dateRange = []
      this.pagination.page = 1
      this.fetchOrderList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchOrderList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchOrderList()
    },
    // 选择改变
    handleSelectionChange(selection) {
      this.selectedOrders = selection
    },
    // 检查是否可选择（只有待发货且已支付的订单可以批量发货）
    checkSelectable(row) {
      return row.order_status === 1 && row.pay_status === 1
    },
    // 批量发货
    handleBatchShip() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择要发货的订单')
        return
      }
      
      // 检查是否都是待发货且已支付的订单
      const invalidOrders = this.selectedOrders.filter(order => 
        order.order_status !== 1 || order.pay_status !== 1
      )
      
      if (invalidOrders.length > 0) {
        this.$message.error('只能对待发货且已支付的订单进行批量发货')
        return
      }
      
      this.batchShipForm = {
        ids: this.selectedOrders.map(order => order.id),
        shipping_company: '',
        shipping_no: '',
        admin_note: ''
      }
      this.batchShipDialogVisible = true
    },
    // 批量发货提交
    handleBatchShipSubmit() {
      this.$refs.batchShipForm.validate(async (valid) => {
        if (valid) {
          try {
            this.batchShipping = true
            const response = await batchShipOrder(this.batchShipForm)
            
            if (response.code === 200) {
              const result = response.data
              
              // 显示详细结果
              if (result.fail_count > 0) {
                let failMessage = `批量发货完成！\n成功：${result.success_count} 个\n失败：${result.fail_count} 个\n\n失败原因：\n`
                result.fail_orders.forEach((item, index) => {
                  failMessage += `${index + 1}. 订单${item.order_no || item.order_id}：${item.reason}\n`
                })
                
                this.$alert(failMessage, '批量发货结果', {
                  confirmButtonText: '确定',
                  type: result.success_count > 0 ? 'warning' : 'error'
                })
              } else {
                this.$message.success(`批量发货成功！共发货 ${result.success_count} 个订单`)
              }
              
              this.batchShipDialogVisible = false
              this.fetchOrderList()
            }
          } catch (error) {
            console.error('批量发货失败:', error)
            this.$message.error('批量发货失败，请重试')
          } finally {
            this.batchShipping = false
          }
        }
      })
    },
    async handleView(row) {
      try {
        const response = await getOrderDetail(row.id)
        this.currentOrder = response.data
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取订单详情失败')
        console.error('获取订单详情失败:', error)
      }
    },
    handleShipFromDetail() {
      this.viewDialogVisible = false
      this.handleShip(this.currentOrder)
    },
    handleShip(row) {
      this.shipForm = {
        id: row.id,
        shipping_company: '',
        shipping_no: '',
        admin_note: ''
      }
      this.shipDialogVisible = true
    },
    handleShipSubmit() {
      this.$refs.shipForm.validate(async (valid) => {
        if (valid) {
          try {
            await shipOrder(this.shipForm)
            this.$message.success('发货成功')
            this.shipDialogVisible = false
            this.fetchOrderList()
          } catch (error) {
            console.error('发货失败:', error)
          }
        }
      })
    },
    handleCancel(row) {
      this.$prompt('请输入取消原因', '取消订单', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea'
      }).then(async ({ value }) => {
        try {
          await cancelOrder({
            id: row.id,
            cancel_reason: value
          })
          this.$message.success('取消成功')
          this.fetchOrderList()
        } catch (error) {
          console.error('取消订单失败:', error)
        }
      })
    },
    handleDelete(row) {
      this.$confirm('确认删除该订单吗？删除后不可恢复！', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteOrder(row.id)
          this.$message.success('删除成功')
          this.fetchOrderList()
        } catch (error) {
          console.error('删除订单失败:', error)
        }
      })
    },
    getStatusType(status) {
      const statusMap = {
        0: 'warning',  // 待付款
        1: 'primary',  // 待发货
        2: 'info',     // 待收货
        3: 'success',  // 已完成
        4: 'danger',   // 已取消
        5: 'warning'   // 售后中
      }
      return statusMap[status] || 'info'
    },
    getStatusText(status) {
      const statusMap = {
        0: '待付款',
        1: '待发货',
        2: '待收货',
        3: '已完成',
        4: '已取消',
        5: '售后中'
      }
      return statusMap[status] || '未知'
    },
    getShippingStatusType(status) {
      const statusMap = {
        0: 'info',     // 未发货
        1: 'warning',  // 已发货
        2: 'success'   // 已收货
      }
      return statusMap[status] || 'info'
    },
    getShippingStatusText(status) {
      const statusMap = {
        0: '未发货',
        1: '已发货',
        2: '已收货'
      }
      return statusMap[status] || '未知'
    },
    getPaymentMethodText(method) {
      const methodMap = {
        'wechat': '微信支付',
        'alipay': '支付宝',
        'balance': '余额支付',
        'verify': '线下核销'
      }
      return methodMap[method] || method || '-'
    },
    getPayStatusType(status) {
      const statusMap = {
        0: 'info',     // 未支付
        1: 'success',  // 已支付
        2: 'warning'   // 已退款
      }
      return statusMap[status] || 'info'
    },
    getPayStatusText(status) {
      const statusMap = {
        0: '未支付',
        1: '已支付',
        2: '已退款'
      }
      return statusMap[status] || '未知'
    },
    handleExport() {
      try {
        this.exporting = true

        // 构建查询参数
        const params = {}

        // 添加搜索条件
        if (this.searchForm.order_no) {
          params.order_no = this.searchForm.order_no
        }
        if (this.searchForm.shop_id) {
          params.shop_id = this.searchForm.shop_id
        }
        if (this.searchForm.user_mobile) {
          params.user_mobile = this.searchForm.user_mobile
        }
        if (this.searchForm.order_status !== '') {
          params.order_status = this.searchForm.order_status
        }
        if (this.searchForm.pay_status !== '') {
          params.pay_status = this.searchForm.pay_status
        }
        if (this.searchForm.shipping_status !== '') {
          params.shipping_status = this.searchForm.shipping_status
        }
        if (this.searchForm.payment_method) {
          params.payment_method = this.searchForm.payment_method
        }
        if (this.searchForm.delivery_type) {
          params.delivery_type = this.searchForm.delivery_type
        }
        // 处理时间范围
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }

        // 显示提示信息
        this.$message({
          message: '正在导出订单，请稍候...',
          type: 'info',
          duration: 2000
        })

        // 走带 token 的请求下载（window.open 带不了 Authorization 头）
        downloadFile('/store.order_controller/export', params, '订单导出.csv').then(() => {
          this.$message({
            message: '导出成功，请检查浏览器下载',
            type: 'success',
            duration: 3000
          })
        }).catch(() => {
          this.$message.error('导出失败，请稍后重试')
        }).finally(() => {
          this.exporting = false
        })
      } catch (error) {
        console.error('导出订单失败:', error)
        this.$message.error('导出失败，请稍后重试')
        this.exporting = false
      }
    },
    async handleStatistics() {
      try {
        const params = {}
        
        // 添加搜索条件
        if (this.searchForm.shop_id) {
          params.shop_id = this.searchForm.shop_id
        }
        // 处理时间范围
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }
        
      const response = await getOrderStatistics(params)
      this.statisticsData = response.data
      this.statisticsDialogVisible = true
      } catch (error) {
        console.error('获取订单统计失败:', error)
        this.$message.error('获取订单统计失败')
      }
    },
    // 同意退款
    handleApproveRefund(row) {
      this.$confirm('确认同意该订单的退款申请吗？退款将直接退回用户支付账户。', '同意退款', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 设置当前订单,用于对话框显示
        this.currentOrder = row
        this.approveRefundForm = {
          id: row.id,
          refund_amount: String(parseFloat(row.paid_amount) || 0),
          admin_note: ''
        }
        this.approveRefundDialogVisible = true
      })
    },
    // 提交同意退款
    handleApproveRefundSubmit() {
      this.$refs.approveRefundForm.validate(async (valid) => {
        if (valid) {
          try {
            await approveRefund(this.approveRefundForm)
            this.$message.success('退款成功')
            this.approveRefundDialogVisible = false
            this.fetchOrderList()
          } catch (error) {
            console.error('退款失败:', error)
            this.$message.error(error.message || '退款失败，请重试')
          }
        }
      })
    },
    // 拒绝退款
    handleRejectRefund(row) {
      this.$prompt('请输入拒绝原因', '拒绝退款', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPattern: /\S+/,
        inputErrorMessage: '拒绝原因不能为空'
      }).then(async ({ value }) => {
        try {
          await rejectRefund({
            id: row.id,
            reject_reason: value,
            admin_note: '管理员拒绝'
          })
          this.$message.success('已拒绝退款')
          this.fetchOrderList()
        } catch (error) {
          console.error('拒绝退款失败:', error)
          this.$message.error(error.message || '拒绝失败，请重试')
        }
      })
    }
  },
  computed: {
    statusStatistics() {
      if (!this.statisticsData) return []
      
      const total = this.statisticsData.total || 1
      
      return [
        {
          label: '待付款',
          count: this.statisticsData.pending_payment || 0,
          amount: this.statisticsData.pending_payment_amount || 0,
          percentage: Math.round(((this.statisticsData.pending_payment || 0) / total) * 100),
          color: '#E6A23C'
        },
        {
          label: '待发货',
          count: this.statisticsData.pending_shipment || 0,
          amount: this.statisticsData.pending_shipment_amount || 0,
          percentage: Math.round(((this.statisticsData.pending_shipment || 0) / total) * 100),
          color: '#409EFF'
        },
        {
          label: '待收货',
          count: this.statisticsData.pending_receipt || 0,
          amount: this.statisticsData.pending_receipt_amount || 0,
          percentage: Math.round(((this.statisticsData.pending_receipt || 0) / total) * 100),
          color: '#909399'
        },
        {
          label: '已完成',
          count: this.statisticsData.completed || 0,
          amount: this.statisticsData.completed_amount || 0,
          percentage: Math.round(((this.statisticsData.completed || 0) / total) * 100),
          color: '#67C23A'
        },
        {
          label: '已取消',
          count: this.statisticsData.cancelled || 0,
          amount: this.statisticsData.cancelled_amount || 0,
          percentage: Math.round(((this.statisticsData.cancelled || 0) / total) * 100),
          color: '#F56C6C'
        },
        {
          label: '售后中',
          count: this.statisticsData.after_sale || 0,
          amount: this.statisticsData.after_sale_amount || 0,
          percentage: Math.round(((this.statisticsData.after_sale || 0) / total) * 100),
          color: '#E6A23C'
        }
      ]
    },
    paymentStatistics() {
      if (!this.statisticsData) return []
      
      const paidCount = this.statisticsData.paid_count || 1
      
      return [
        {
          label: '微信支付',
          count: this.statisticsData.wechat_count || 0,
          amount: this.statisticsData.wechat_amount || 0,
          percentage: paidCount > 0 ? Math.round(((this.statisticsData.wechat_count || 0) / paidCount) * 100) : 0,
          color: '#67C23A'
        },
        {
          label: '支付宝',
          count: this.statisticsData.alipay_count || 0,
          amount: this.statisticsData.alipay_amount || 0,
          percentage: paidCount > 0 ? Math.round(((this.statisticsData.alipay_count || 0) / paidCount) * 100) : 0,
          color: '#409EFF'
        },
        {
          label: '余额支付',
          count: this.statisticsData.balance_count || 0,
          amount: this.statisticsData.balance_amount || 0,
          percentage: paidCount > 0 ? Math.round(((this.statisticsData.balance_count || 0) / paidCount) * 100) : 0,
          color: '#E6A23C'
        }
      ]
    }
  }
}
</script>

<style scoped>
.order-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.header-actions .el-button {
  font-weight: 500;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .ship-btn {
  color: #67c23a;
}

.table-operations .ship-btn:hover {
  color: #85ce61;
  background-color: #f0f9ff;
}

.table-operations .cancel-btn {
  color: #e6a23c;
}

.table-operations .cancel-btn:hover {
  color: #ebb563;
  background-color: #fdf6ec;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.dialog-footer {
  text-align: right;
}

.order-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

.amount-text {
  color: #409EFF;
  font-weight: 600;
}

.amount-highlight {
  color: #f56c6c;
  font-size: 18px;
  font-weight: bold;
}

.free-shipping {
  color: #67c23a;
  font-weight: 600;
}

.remark-content {
  color: #606266;
  white-space: pre-wrap;
  line-height: 1.6;
  min-height: 40px;
}

.admin-remark {
  color: #409EFF;
  background-color: #ecf5ff;
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

.goods-list {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.statistics-content {
  padding: 10px 0;
}

.statistics-card {
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.statistics-card .statistics-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 10px;
}

.statistics-card .statistics-value {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
}

.statistics-section {
  margin-top: 30px;
}

.statistics-section .section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.count-text {
  font-weight: 600;
  color: #409EFF;
}

.statistics-card:nth-of-type(1) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.statistics-card:nth-of-type(2) {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.statistics-card:nth-of-type(3) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.statistics-card:nth-of-type(4) {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.form-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.form-tip i {
  margin-right: 4px;
}
</style>